import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ExtendedRight, IOptions, SearchOfIndicators } from '../../../../interfaces/Rights';
import { getExtendedRight, getOptions, getSearchOfIndicators } from './filterAndSearchThunk';
import { ODSPriorityGroups } from '../../../../interfaces/Indicator';
import { FilterSelection, resolveFilterTree, uniqueByTitle } from '../../../../views/IndicatorDisplay/utils/filterTree';

interface IndicatorDisplayFilter {
    loading: "idle" | "pending" | "succeeded" | "failed";
    loadingExtendedRight: "idle" | "pending" | "succeeded" | "failed";
    loadingOptions: "idle" | "pending" | "succeeded" | "failed";
    // --> START DATA FOR SUPER FILTER <--
    rightOptions: IOptions[] | [];
    rights: ExtendedRight[];
    selectedRight: ExtendedRight[] | null;
    selectedIndicatorType: string[] | null;
    indicatorTypesOptions: IOptions[];
    dimensionOptions: IOptions[] | null;
    subDimensionOptions: IOptions[] | null;
    indicatorsOptions: IOptions[] | null;

    // --> END DATA FOR SUPER FILTER <--
    // --> START DATA FOR SEARCH <--
    searchOfIndicators: SearchOfIndicators[] | null | undefined;
    loadingSearch: boolean;
    errorSearch: string | undefined | null;
    // --> START DATA FOR SEARCH <--
    periodSelected: string,
    ods: ODSPriorityGroups[],
    priority_groups: ODSPriorityGroups[],
    ODSOptions: IOptions[],
    priorityGroupsOptions: IOptions[]
}

const initialState: IndicatorDisplayFilter = {
    loading: 'idle',
    loadingExtendedRight: 'idle',
    loadingOptions: 'idle',
    indicatorTypesOptions: [{ value: 'Resultados', id: 0, label: 'Resultados' }, { value: 'Proceso', label: 'Proceso', id: 1 }, { value: 'Estructurales', label: 'Estructurales', id: 2 }],
    rightOptions: [],
    rights: [],
    selectedRight: null,
    selectedIndicatorType: null,
    dimensionOptions: null,
    subDimensionOptions: null,
    indicatorsOptions: null,
    searchOfIndicators: null,
    loadingSearch: false,
    errorSearch: '',
    periodSelected: '',
    ods: [],
    priority_groups: [],
    ODSOptions: [],
    priorityGroupsOptions: []
}

//REUSABLE FUNCTIONS
const normalize = (value: string) => value.toLowerCase().trim();

const filterByTitles = <T extends { titulo: string }>(items: T[], titles: string[]): T[] => {
    const normalizedTitles = titles.map(normalize);
    return items.filter(item => normalizedTitles.includes(normalize(item.titulo)));
};

const createOptions = (items: { titulo?: string; nombre?: string; id: number }[]): IOptions[] => {
    return items.map(({ titulo, nombre, id }) => ({
        value: titulo ?? nombre ?? '',
        label: titulo ?? nombre ?? '',
        id
    }));
};

export const indicatorDisplayFilterSlice = createSlice({
    name: 'indicator_display_filter',
    initialState,
    reducers: {
        // arma todas las opciones del filtro de una vez, a partir de los derechos y lo seleccionado.
        setFilterSelection: (state, action: PayloadAction<{ rights: string[]; selection: FilterSelection }>) => {
            const { rights, selection } = action.payload;

            const selectedRights: ExtendedRight[] = JSON.parse(JSON.stringify(filterByTitles(state.rights, rights)));
            const { all } = resolveFilterTree(selectedRights, selection);

            // una opción por nombre: las dimensiones/subdimensiones/indicadores repetidos se unifican
            const toOptions = (items: { id: number; titulo: string }[]) =>
                items.length ? createOptions(uniqueByTitle(items)) : null;

            state.selectedRight = selectedRights.length ? selectedRights : null;
            state.selectedIndicatorType = selection.types.length ? selection.types : null;
            state.dimensionOptions = toOptions(all.dimensions);
            state.subDimensionOptions = toOptions(all.subdimensions);
            state.indicatorsOptions = toOptions(all.indicators);
        },
        cleanOptions: (state, action: PayloadAction<'dimension' | 'subdimension' | 'indicators'>) => {
            switch (action.payload) {
                case 'dimension':
                    state.dimensionOptions = null;
                    state.subDimensionOptions = null;
                    state.indicatorsOptions = null;
                    break
                case 'subdimension':
                    state.subDimensionOptions = null;
                    state.indicatorsOptions = null;
                    break
                case 'indicators':
                    state.indicatorsOptions = null;
                    break
            }
        },
        cleanSearch: (state) => {
            state.errorSearch = null;
            state.searchOfIndicators = [];
        },
    },

    extraReducers: (builder) => {
        builder.addCase(getExtendedRight.pending, (state) => {
            state.loadingExtendedRight = "pending";
        });
        builder.addCase(getExtendedRight.fulfilled, (state, action) => {
            state.loadingExtendedRight = "succeeded";
            const right = action.payload.right;
            state.selectedRight = Array.isArray(right) ? right : right ? [right] : null;
        });
        builder.addCase(getExtendedRight.rejected, (state) => {
            state.loadingExtendedRight = "failed";
        });
        builder.addCase(getOptions.pending, (state) => {
            state.loadingOptions = "pending";
        });
        builder.addCase(getOptions.fulfilled, (state, action) => {
            state.loadingOptions = "succeeded";
            state.rights = action.payload.rights ?? [];
            state.rightOptions = action.payload.rights.map((right) => ({
                label: right.titulo,
                value: right.titulo,
                id: right.id
            }));
            state.priority_groups = action.payload.grupos_prioritarios
            state.ods = action?.payload?.ods
            state.ODSOptions = action?.payload?.ods
                ? action.payload.ods.map(ods => ({
                    label: `${ods.numero} - ${ods.nombre}`,
                    value: `${ods.numero} - ${ods.nombre}`,
                    id: ods.id
                }))
                : [];
            state.priorityGroupsOptions = action.payload.grupos_prioritarios
                ? action.payload.grupos_prioritarios.map((groups) => (
                    {
                        id: groups.id,
                        value: groups.nombre,
                        label: groups.nombre
                    })
                )
                : [];
        });
        builder.addCase(getOptions.rejected, (state) => {
            state.loadingOptions = "failed";
        });
        builder.addCase(getSearchOfIndicators.pending, (state) => {
            state.loadingSearch = true;
        });
        builder.addCase(getSearchOfIndicators.fulfilled, (state, action) => {
            state.loadingSearch = false;
            state.searchOfIndicators = action.payload.search;
            state.errorSearch = action.payload.search && action.payload.search?.length > 0 ? null : 'No se encontraron indicadores.';
        });
        builder.addCase(getSearchOfIndicators.rejected, (state, action) => {
            state.errorSearch = action.error.message
            state.loadingSearch = false;
        });
    }
});

export const {
    setFilterSelection,
    cleanOptions,
    cleanSearch,
} =indicatorDisplayFilterSlice.actions;

export default indicatorDisplayFilterSlice.reducer;